import React from 'react';
import { CUR } from './api';
import { useCountUp } from './useCountUp';

const CAT_COLORS = ['#84cc16', '#38bdf8', '#a78bfa', '#fb923c', '#f472b6', '#2dd4bf', '#facc15', '#f87171', '#60a5fa', '#c084fc', '#4ade80', '#fbbf24', '#94a3b8'];

// Semicircle 0–100 health gauge (SVG).
export const HealthGauge = ({ score = 0, label = '', delta = 0 }) => {
  const shown = useCountUp(score, 1100);
  const R = 90;
  const CX = 110;
  const CY = 110;
  const pct = Math.max(0, Math.min(100, shown)) / 100;
  const angle = Math.PI * (1 - pct);
  const x = CX + R * Math.cos(angle);
  const y = CY - R * Math.sin(angle);
  const large = pct > 0.5 ? 1 : 0;
  const color = score >= 80 ? '#84cc16' : score >= 65 ? '#38bdf8' : score >= 45 ? '#fbbf24' : '#f87171';
  return (
    <div className="flex flex-col items-center" data-testid="cfo-health-gauge">
      <svg viewBox="0 0 220 130" className="w-full max-w-[260px]">
        <path d={`M 20 110 A ${R} ${R} 0 0 1 200 110`} fill="none" stroke="rgba(15,23,42,0.08)" strokeWidth="16" strokeLinecap="round" />
        <path d={`M 20 110 A ${R} ${R} 0 ${large} 1 ${x} ${y}`} fill="none" stroke={color} strokeWidth="16" strokeLinecap="round" style={{ transition: 'all 900ms cubic-bezier(.4,0,.2,1)', filter: `drop-shadow(0 0 5px ${color})` }} />
        <text x="110" y="96" textAnchor="middle" className="fill-slate-900 font-fin" style={{ fontSize: 44, fontWeight: 800 }}>{Math.round(shown)}</text>
        <text x="110" y="118" textAnchor="middle" className="fill-slate-400 font-fin" style={{ fontSize: 12, letterSpacing: 1 }}>/ 100</text>
      </svg>
      <div className="text-center -mt-1">
        <div className="text-base font-extrabold" style={{ color }}>{label}</div>
        {delta !== 0 && (
          <div className={`text-xs font-semibold ${delta > 0 ? 'text-lime-400' : 'text-rose-400'}`}>
            {delta > 0 ? '▲' : '▼'} {Math.abs(delta)} pts vs last period
          </div>
        )}
        {delta === 0 && <div className="text-xs text-slate-500">Steady vs last period</div>}
      </div>
    </div>
  );
};

// 12-month grouped bar chart: income vs expense.
export const MoneyFlow = ({ data = [], currency }) => {
  const max = Math.max(1, ...data.map((d) => Math.max(d.income, d.expense)));
  return (
    <div data-testid="cfo-money-flow">
      <div className="flex items-end gap-2 sm:gap-3 h-44 pt-2">
        {data.map((d) => (
          <div key={d.month} className="flex-1 flex flex-col items-center justify-end gap-1 group relative min-w-0">
            <div className="absolute -top-1 hidden lg:block opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 text-white text-[10px] rounded px-1.5 py-1 whitespace-nowrap z-10 pointer-events-none">
              In {CUR(d.income, currency)} · Out {CUR(d.expense, currency)}
            </div>
            <div className="w-full flex items-end justify-center gap-0.5 h-full">
              <div className="w-1/2 rounded-t bg-lime-400 transition-all" style={{ height: `${(d.income / max) * 100}%`, minHeight: d.income ? 3 : 0, boxShadow: d.income ? '0 0 8px rgba(163,230,53,0.45)' : 'none' }} />
              <div className="w-1/2 rounded-t bg-rose-400/80 transition-all" style={{ height: `${(d.expense / max) * 100}%`, minHeight: d.expense ? 3 : 0 }} />
            </div>
            <span className="text-[9px] sm:text-[10px] text-slate-400 font-medium">{d.label}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-3 text-[11px] text-slate-400">
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-lime-400" /> Money in</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-rose-400/80" /> Money out</span>
      </div>
    </div>
  );
};

// Where the money went — horizontal category bars.
export const CategoryBreakdown = ({ rows = [], currency, onPick }) => {
  const max = Math.max(1, ...rows.map((r) => r.amount));
  if (!rows.length) return <div className="text-sm text-slate-400 py-8 text-center">No spending in this period yet.</div>;
  return (
    <div className="space-y-2.5" data-testid="cfo-category-breakdown">
      {rows.map((r, i) => (
        <button key={r.category} onClick={() => onPick?.(r.category)} data-testid={`cat-row-${r.category}`}
          className="w-full text-left group">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-semibold text-slate-700">{r.category}</span>
            <span className="tabular-nums text-slate-400 font-fin">{CUR(r.amount, currency)} · {r.pct}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full rounded-full transition-all group-hover:brightness-110" style={{ width: `${(r.amount / max) * 100}%`, background: CAT_COLORS[i % CAT_COLORS.length] }} />
          </div>
        </button>
      ))}
    </div>
  );
};

export { CAT_COLORS };

// Compact cumulative-savings area sparkline (SVG).
export const SavingsTimelineChart = ({ series = [], currency }) => {
  if (series.length < 2) return null;
  const W = 640;
  const H = 120;
  const P = { l: 6, r: 6, t: 12, b: 20 };
  const max = Math.max(1, ...series.map((p) => p.cumulative));
  const x = (i) => P.l + (i / (series.length - 1)) * (W - P.l - P.r);
  const y = (v) => P.t + (1 - v / max) * (H - P.t - P.b);
  const pts = series.map((p, i) => `${x(i)},${y(p.cumulative)}`);
  const line = `M ${pts.join(' L ')}`;
  const area = `M ${x(0)},${H - P.b} L ${pts.join(' L ')} L ${x(series.length - 1)},${H - P.b} Z`;
  return (
    <div data-testid="cfo-savings-timeline">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height: 120 }}>
        <defs>
          <linearGradient id="svArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#34d399" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#34d399" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#svArea)" />
        <path d={line} fill="none" stroke="#34d399" strokeWidth="2.5" strokeLinejoin="round" style={{ filter: 'drop-shadow(0 0 6px #34d399)' }} />
        {series.map((p, i) => i === series.length - 1 && <circle key={i} cx={x(i)} cy={y(p.cumulative)} r="4" fill="#34d399" stroke="#050811" strokeWidth="2" />)}
      </svg>
      <div className="flex justify-between text-[10px] text-slate-500 -mt-1 px-1">
        {series.map((p) => <span key={p.month}>{p.label}</span>)}
      </div>
    </div>
  );
};

// Projected cash line over 12 months (SVG). Shaded area, "now" split.
export const CashForecastChart = ({ series = [], currency }) => {
  if (!series.length) return null;
  const W = 720;
  const H = 200;
  const P = { l: 8, r: 8, t: 16, b: 24 };
  const vals = series.map((p) => p.cash);
  const min = Math.min(0, ...vals);
  const max = Math.max(1, ...vals);
  const span = max - min || 1;
  const x = (i) => P.l + (i / (series.length - 1)) * (W - P.l - P.r);
  const y = (v) => P.t + (1 - (v - min) / span) * (H - P.t - P.b);
  const pts = series.map((p, i) => `${x(i)},${y(p.cash)}`);
  const line = `M ${pts.join(' L ')}`;
  const area = `M ${x(0)},${y(min)} L ${pts.join(' L ')} L ${x(series.length - 1)},${y(min)} Z`;
  const zeroY = y(0);
  const endNeg = series[series.length - 1].cash < 0;
  const stroke = endNeg ? '#f87171' : '#84cc16';
  return (
    <div data-testid="cfo-forecast-chart">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" style={{ height: 200 }}>
        <defs>
          <linearGradient id="fcArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.35" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
        </defs>
        {min < 0 && max > 0 && <line x1={P.l} y1={zeroY} x2={W - P.r} y2={zeroY} stroke="rgba(248,113,113,0.5)" strokeWidth="1" strokeDasharray="4 4" />}
        <path d={area} fill="url(#fcArea)" />
        <path d={line} fill="none" stroke={stroke} strokeWidth="2.5" strokeLinejoin="round" style={{ filter: `drop-shadow(0 0 6px ${stroke})` }} />
        {series.map((p, i) => (i === 0 || i === series.length - 1) && (
          <circle key={i} cx={x(i)} cy={y(p.cash)} r="3.5" fill={i === 0 ? '#fff' : stroke} stroke={stroke} strokeWidth="2" />
        ))}
      </svg>
      <div className="flex justify-between text-[10px] text-slate-400 -mt-2 px-1">
        {series.filter((_, i) => i % 2 === 0).map((p) => <span key={p.month}>{p.label}</span>)}
      </div>
    </div>
  );
};
